import { useEffect, useRef } from 'react';
import type { StartChatTarget } from '@chat-emp/shared';

import { Avatar } from '../components/Avatar.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { useCompose } from './useCompose.js';
import styles from './ComposeView.module.css';

interface ComposeViewProps {
  target: StartChatTarget;
  employerId: string;
  /** The host's suggested opener. Used only when no saved draft exists — a
   *  stored draft is what the employer actually wrote, and always wins. */
  initialDraft?: string;
  onSent: (channelId: string) => void;
  onCancel: () => void;
}

/**
 * Writing to someone the chat system has never heard of.
 *
 * There is no channel behind this view and there will not be one until they
 * send. That is the point: under the old flow the host created a channel first,
 * so an employer who thought better of it left an empty conversation behind and
 * every Chat button for that person flipped to "View Chat" pointing at nothing.
 */
export function ComposeView({
  target,
  employerId,
  initialDraft,
  onSent,
  onCancel,
}: ComposeViewProps) {
  const { draft, setDraft, send, sending, error } = useCompose(target, employerId);

  // Seed once per target. Running on every render would fight the typist, and
  // re-seeding after they cleared the box would put the suggestion back.
  const seeded = useRef<StartChatTarget | null>(null);
  useEffect(() => {
    if (seeded.current === target) return;
    seeded.current = target;
    // A saved draft is theirs; the host's opener is only a suggestion.
    if (initialDraft && !draft) setDraft(initialDraft);
  }, [target, initialDraft, draft, setDraft]);

  async function submit() {
    const channelId = await send(draft);
    if (channelId) onSent(channelId);
  }

  return (
    <div className={styles.compose}>
      <div className={styles.to}>
        <Avatar
          name={target.applicant.name}
          src={target.applicant.image}
          locked={target.applicant.locked}
          size={32}
        />
        <span className={styles.toText}>
          <span className={styles.toName}>{target.applicant.name}</span>
          <span className={styles.toHint}>No messages yet</span>
        </span>
        <button className={styles.cancel} onClick={onCancel} aria-label="Cancel">
          <Icon name="close" size={16} />
        </button>
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.editor}>
        <textarea
          className={styles.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Shift+Enter is a newline — an employer writing a two-line opener
            // should not have it sent halfway through.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={`Write to ${target.applicant.name}…`}
          aria-label="Message"
          rows={4}
        />
        <div className={styles.actions}>
          <Button onClick={() => void submit()} disabled={sending || draft.trim() === ''}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
